import { useRef, useState } from "react";
import { useDesignMode } from "@/hooks/useDesignMode";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import GlobalTypographySheet from "./GlobalTypographySheet";
import {
  Paintbrush,
  Save,
  Undo2,
  Loader2,
  Upload,
  EyeOff,
} from "lucide-react";

const DesignModeToolbar = () => {
  const {
    isDesignMode,
    toggleDesignMode,
    isAdmin,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    pendingDomOverrides,
    clearPendingDomOverrides,
    elements,
    setElements,
    selectElement,
  } = useDesignMode();
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isAdmin) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const overlayElements = elements.filter((el) => el.type === "overlay");
      for (const el of overlayElements) {
        const { error } = await supabase
          .from("page_overlays")
          .update({
            position_x: el.position_x,
            position_y: el.position_y,
            width: el.width,
            height: el.height,
            rotation: el.rotation,
            opacity: el.opacity,
            z_index: el.z_index,
            is_active: el.is_active ?? true,
            page: el.page ?? "all",
            label: el.label ?? "",
            position_type: el.positionType ?? "fixed",
            updated_at: new Date().toISOString(),
          })
          .eq("id", el.id);
        if (error) throw error;
      }

      const domOverrides = Object.values(pendingDomOverrides);
      if (domOverrides.length > 0) {
        const { data: userData } = await supabase.auth.getUser();
        const { error } = await supabase
          .from("design_dom_overrides")
          .upsert(
            domOverrides.map((override) => ({
              selector: override.selector,
              page: override.page || "all",
              tag_name: override.tagName,
              text_content: override.textContent,
              styles: override.styles,
              updated_by: userData.user?.id ?? null,
              updated_at: new Date().toISOString(),
            })),
            { onConflict: "selector,page" }
          );
        if (error) throw error;
      }
      clearPendingDomOverrides();
      setHasUnsavedChanges(false);
      toast({ title: "✅ Design sauvegardé" });
    } catch {
      toast({ title: "Erreur de sauvegarde", variant: "destructive" });
    }
    setSaving(false);
  };

  const handleCancel = () => {
    window.location.reload();
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop();
    const path = `overlays/${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("theme-assets")
      .upload(path, file);

    if (uploadError) {
      toast({ title: "Erreur d'upload", description: uploadError.message, variant: "destructive" });
      return;
    }

    const { data: urlData } = supabase.storage.from("theme-assets").getPublicUrl(path);

    const newOverlay = {
      page: "all",
      image_url: urlData.publicUrl,
      position_x: 50,
      position_y: 30,
      width: 150,
      height: 150,
      rotation: 0,
      opacity: 80,
      z_index: elements.length,
      is_active: true,
      label: file.name.replace(/\.[^.]+$/, ""),
    };

    const { data, error } = await supabase
      .from("page_overlays")
      .insert(newOverlay)
      .select()
      .single();

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    if (!data) return;

    const d = data;
    setElements((prev) => [
      ...prev,
      {
        id: d.id,
        type: "overlay" as const,
        position_x: d.position_x,
        position_y: d.position_y,
        width: d.width,
        height: d.height,
        rotation: d.rotation,
        opacity: d.opacity,
        z_index: d.z_index,
        image_url: d.image_url,
        page: d.page,
        label: d.label ?? undefined,
        is_active: d.is_active,
      },
    ]);
    selectElement(d.id);
    toast({ title: "✅ Image ajoutée ! Glissez-la pour la positionner." });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!isDesignMode) {
    return (
      <button
        data-design-toolbar
        data-editor-ui
        onClick={toggleDesignMode}
        className="fixed bottom-4 right-4 z-[9999] bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:scale-110 transition-transform"
        title="Activer le mode design"
      >
        <Paintbrush className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div
      data-design-toolbar
      data-editor-ui
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] bg-card border border-border rounded-2xl shadow-2xl px-4 py-2 flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1 pr-3 border-r border-border">
        <Paintbrush className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Mode Design</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleUploadImage}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        title="Ajouter une image"
      >
        <Upload className="w-4 h-4 mr-1" />
        Image
      </Button>

      <GlobalTypographySheet />



      {hasUnsavedChanges && (
        <>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            <Undo2 className="w-4 h-4 mr-1" />
            Annuler
          </Button>
        </>
      )}

      <Button size="sm" variant="ghost" onClick={toggleDesignMode}>
        <EyeOff className="w-4 h-4 mr-1" />
        Quitter
      </Button>
    </div>
  );
};

export default DesignModeToolbar;
